import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  collection, getDocs, addDoc, updateDoc, doc,
  query, where, Timestamp
} from "firebase/firestore";
import { db } from "../firebase";
import { getAuth } from "firebase/auth";
import { Spin } from "antd";

const TOTAL_LOCKERS = 20;
const COL_SIZE = 10;

export default function CasierPage() {
  const navigate = useNavigate();
  const auth = getAuth();
  const [dark, setDark] = useState(() => localStorage.getItem("ws-theme") === "dark");

  const [loading, setLoading]           = useState(true);
  const [fullName, setFullName]         = useState("");
  const [isSubscriber, setIsSubscriber] = useState(false);
  const [myLocker, setMyLocker]         = useState(null); // locker already assigned to me
  const [lockers, setLockers]           = useState([]); // [{id, number, users:[]}]
  const [selected, setSelected]         = useState(null); // locker being confirmed
  const [confirming, setConfirming]     = useState(false);
  const [animating, setAnimating]       = useState(false); // success animation
  const [assignedLocker, setAssignedLocker] = useState(null);
  const [error, setError]               = useState("");

  const toggleTheme = () => {
    setDark(d => { localStorage.setItem("ws-theme", !d ? "dark" : "light"); return !d; });
  };
  const t = dark ? theme.dark : theme.light;

  useEffect(() => {
    const init = async () => {
      const user = auth.currentUser;
      if (!user) { setLoading(false); return; }

      // 1 — Contact / name
      try {
        const cSnap = await getDocs(query(collection(db, "contact"), where("FirebaseId", "==", user.uid)));
        if (!cSnap.empty) setFullName(cSnap.docs[0].data().fullname || cSnap.docs[0].data().name || "");
      } catch {}

      // 2 — Subscription check
      try {
        const mSnap = await getDocs(query(collection(db, "members"), where("FirebaseId", "==", user.uid)));
        setIsSubscriber(!mSnap.empty);
      } catch {}

      // 3 — Load lockers collection
      try {
        const lSnap = await getDocs(collection(db, "casiers"));
        const data = lSnap.docs.map(d => ({ docId: d.id, ...d.data() }));

        // Build full grid 1-20, merge with Firestore data
        const grid = Array.from({ length: TOTAL_LOCKERS }, (_, i) => {
          const num = i + 1;
          const found = data.find(d => d.number === num);
          return found || { number: num, users: [], docId: null };
        });
        setLockers(grid);

        // Check if I already have a locker
        const mine = data.find(d => d.users?.some(u => u.uid === user.uid));
        if (mine) setMyLocker(mine);
      } catch (err) { console.error(err); }

      setLoading(false);
    };
    init();
  }, []);

  const handleAssign = async () => {
    if (!selected) return;
    const user = auth.currentUser;
    if (!user) return;
    setConfirming(true);
    setError("");

    try {
      const lockerData = selected;
      const newUser = { uid: user.uid, name: fullName, assignedAt: new Date().toISOString() };

      if (lockerData.docId) {
        // Locker exists in Firestore — add user
        await updateDoc(doc(db, "casiers", lockerData.docId), {
          users: [...(lockerData.users || []), newUser],
        });
      } else {
        // Create new locker doc
        await addDoc(collection(db, "casiers"), {
          number: lockerData.number,
          users:  [newUser],
          createdAt: Timestamp.fromDate(new Date()),
        });
      }

      // Update local state
      const updatedLockers = lockers.map(l =>
        l.number === lockerData.number
          ? { ...l, users: [...(l.users || []), newUser] }
          : l
      );
      setLockers(updatedLockers);
      setMyLocker({ ...lockerData, users: [...(lockerData.users || []), newUser] });
      setSelected(null);

      // Trigger animation
      setAssignedLocker(lockerData.number);
      setAnimating(true);
      setTimeout(() => setAnimating(false), 3000);

    } catch (err) {
      console.error(err);
      setError("Erreur lors de l'attribution. Réessayez.");
    }
    setConfirming(false);
  };

  const getLockerStatus = (locker) => {
    const users = locker.users || [];
    if (users.some(u => u.uid === auth.currentUser?.uid)) return "mine";
    if (users.length >= 2) return "full";
    if (users.length === 1) return "partial";
    return "free";
  };

  const statusColor = (status, t) => ({
    mine:    { bg: t.blue,   border: t.blue,   text: "#fff" },
    full:    { bg: t.red,    border: t.red,    text: "#fff" },
    partial: { bg: t.amber,  border: t.amber,  text: "#fff" },
    free:    { bg: "#1a1a1a",border: "#333",   text: "#888" },
  }[status]);

  // Split into 2 columns
  const col1 = lockers.slice(0, 10);  // 1-10
  const col2 = lockers.slice(10, 20); // 11-20

  return (
    <div style={{ background: t.bg, minHeight: "100vh" }}>
      <div style={{ maxWidth: 480, margin: "0 auto", paddingBottom: 40 }}>

        {/* Topbar */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 18px 0" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <img src="/logo.PNG" alt="WeStudy" style={{ height: 36, objectFit: "contain" }} onError={e => e.target.style.display = "none"} />
            <div>
              <div style={{ fontSize: 14, fontWeight: 600, color: t.text1 }}>Casiers</div>
              <div style={{ fontSize: 11, color: t.text2 }}>Espace de rangement sécurisé</div>
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <button onClick={toggleTheme} style={{ ...s.toggleTrack, background: t.bg3, border: `1px solid ${t.border}` }}>
              <div style={{ ...s.toggleKnob, background: t.blue, transform: dark ? "translateX(16px)" : "translateX(0)" }} />
            </button>
            <button onClick={() => navigate("/")} style={{ fontSize: 12, borderRadius: 8, padding: "6px 10px", cursor: "pointer", background: t.bg3, border: `1px solid ${t.border}`, color: t.text2 }}>← Retour</button>
          </div>
        </div>

        {loading ? (
          <div style={{ textAlign: "center", padding: "60px 0" }}><Spin /></div>
        ) : !isSubscriber ? (

          /* Non subscriber */
          <div style={{ margin: "24px 18px", background: t.redBg, border: `1px solid ${t.redBorder}`, borderRadius: 16, padding: 20, textAlign: "center" }}>
            <div style={{ fontSize: 36, marginBottom: 10 }}>🔒</div>
            <div style={{ fontSize: 15, fontWeight: 700, color: t.red, marginBottom: 8 }}>Abonnement requis</div>
            <div style={{ fontSize: 13, color: t.text2, lineHeight: 1.6 }}>
              Les casiers sont réservés aux membres avec un abonnement actif. Souscrivez un abonnement pour accéder à cette fonctionnalité.
            </div>
            <button onClick={() => navigate("/payment")}
              style={{ marginTop: 16, padding: "10px 20px", borderRadius: 10, border: "none", background: t.blue, color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
              💳 Voir les abonnements
            </button>
          </div>

        ) : (
          <div style={{ padding: "16px 18px 0" }}>

            {/* My locker banner */}
            {myLocker && (
              <div style={{ background: t.blueBg, border: `1px solid ${t.blue}44`, borderRadius: 14, padding: "14px 16px", marginBottom: 14, display: "flex", alignItems: "center", gap: 12 }}>
                <div style={{ width: 44, height: 44, background: t.blue, borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 18, fontWeight: 800, flexShrink: 0 }}>
                  {myLocker.number}
                </div>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: t.blueText }}>Mon casier · #{myLocker.number}</div>
                  <div style={{ fontSize: 11, color: t.text2, marginTop: 2 }}>
                    Valide tant que votre abonnement est actif · {myLocker.users?.length || 1}/2 personne(s)
                  </div>
                </div>
              </div>
            )}

            {/* Legend */}
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 14 }}>
              {[
                { color: "#1a1a1a", border: "#333", label: "Libre" },
                { color: t.amber,   border: t.amber,  label: "1 personne" },
                { color: t.red,     border: t.red,    label: "Complet" },
                { color: t.blue,    border: t.blue,   label: "Mon casier" },
              ].map(item => (
                <div key={item.label} style={{ display: "flex", alignItems: "center", gap: 5 }}>
                  <div style={{ width: 14, height: 14, borderRadius: 3, background: item.color, border: `1px solid ${item.border}` }} />
                  <span style={{ fontSize: 10, color: t.text3 }}>{item.label}</span>
                </div>
              ))}
            </div>

            {/* Success animation overlay */}
            {animating && (
              <div style={{ position: "fixed", inset: 0, zIndex: 2000, background: "rgba(0,0,0,0.85)", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", animation: "fadeIn 0.3s ease" }}>
                <div style={{ animation: "popIn 0.5s cubic-bezier(0.34,1.56,0.64,1)" }}>
                  <div style={{ width: 120, height: 120, background: "#111", border: "3px solid #333", borderRadius: 16, display: "flex", alignItems: "center", justifyContent: "center", position: "relative", boxShadow: "0 0 40px rgba(37,99,235,0.6), inset 0 0 20px rgba(0,0,0,0.8)" }}>
                    {/* Steel texture lines */}
                    {[20,35,50,65,80,95].map(y => (
                      <div key={y} style={{ position: "absolute", left: 0, right: 0, top: `${y}px`, height: 1, background: "rgba(255,255,255,0.04)" }} />
                    ))}
                    {/* Handle */}
                    <div style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", width: 6, height: 24, borderRadius: 3, background: "#444", boxShadow: "inset 0 1px 2px rgba(255,255,255,0.1)" }} />
                    {/* Number bottom right */}
                    <div style={{ position: "absolute", bottom: 8, right: 10, fontSize: 10, color: "#666", fontWeight: 700 }}>{assignedLocker}</div>
                    {/* Open door effect */}
                    <div style={{ fontSize: 36, animation: "unlock 0.6s ease 0.3s both" }}>🔓</div>
                    {/* Blue glow ring */}
                    <div style={{ position: "absolute", inset: -8, borderRadius: 22, border: "2px solid #2563eb", animation: "glowPulse 1s ease infinite" }} />
                  </div>
                </div>
                <div style={{ marginTop: 24, fontSize: 20, fontWeight: 800, color: "#fff", letterSpacing: 1, animation: "slideUp 0.4s ease 0.4s both" }}>
                  Casier #{assignedLocker} attribué !
                </div>
                <div style={{ marginTop: 8, fontSize: 13, color: "#94a3b8", animation: "slideUp 0.4s ease 0.6s both" }}>
                  Valide toute la durée de votre abonnement
                </div>
                {/* Confetti dots */}
                {[...Array(12)].map((_, i) => (
                  <div key={i} style={{
                    position: "fixed",
                    top: `${20 + Math.random() * 60}%`,
                    left: `${10 + Math.random() * 80}%`,
                    width: 8, height: 8, borderRadius: "50%",
                    background: ["#2563eb","#4ade80","#fbbf24","#f87171","#a78bfa"][i % 5],
                    animation: `confetti${i % 3} 1.5s ease ${i * 0.1}s both`,
                  }} />
                ))}
              </div>
            )}

            {/* Locker grid — 2 columns side by side */}
            <div style={{ display: "flex", gap: 12, justifyContent: "center" }}>
              {[col1, col2].map((col, ci) => (
                <div key={ci} style={{
                  display: "flex", flexDirection: "column", gap: 6,
                  background: "#111",
                  border: "3px solid #222",
                  borderRadius: 12,
                  padding: 10,
                  boxShadow: "inset 0 2px 8px rgba(0,0,0,0.8), 0 4px 20px rgba(0,0,0,0.4)",
                  flex: 1,
                  maxWidth: 180,
                }}>
                  {/* Column top bar */}
                  <div style={{ height: 8, background: "#222", borderRadius: 4, marginBottom: 4, boxShadow: "inset 0 1px 3px rgba(0,0,0,0.5)" }} />

                  {col.map(locker => {
                    const status = getLockerStatus(locker);
                    const colors = statusColor(status, t);
                    const isMine = status === "mine";
                    const isFull = status === "full";
                    const isPartial = status === "partial";
                    const canSelect = !isFull && !isMine && !myLocker;

                    return (
                      <div key={locker.number}
                        onClick={() => canSelect && setSelected(locker)}
                        style={{
                          position: "relative",
                          height: 52,
                          background: isMine ? "#1e3a5f" : isFull ? "#2d1515" : isPartial ? "#2a1e07" : "#1a1a1a",
                          border: `1.5px solid ${isMine ? t.blue : isFull ? "#7f1d1d" : isPartial ? "#78350f" : "#2a2a2a"}`,
                          borderRadius: 6,
                          cursor: canSelect ? "pointer" : "default",
                          transition: "all 0.15s",
                          boxShadow: isMine
                            ? `0 0 12px rgba(37,99,235,0.4), inset 0 1px 0 rgba(255,255,255,0.05)`
                            : `inset 0 2px 4px rgba(0,0,0,0.6), inset 0 -1px 0 rgba(255,255,255,0.02)`,
                          overflow: "hidden",
                        }}
                        onMouseEnter={e => { if (canSelect) e.currentTarget.style.borderColor = t.blue; }}
                        onMouseLeave={e => { if (canSelect) e.currentTarget.style.borderColor = isMine ? t.blue : isFull ? "#7f1d1d" : isPartial ? "#78350f" : "#2a2a2a"; }}
                      >
                        {/* Steel texture horizontal lines */}
                        {[14, 28, 42].map(y => (
                          <div key={y} style={{ position: "absolute", left: 0, right: 0, top: y, height: 1, background: "rgba(255,255,255,0.03)" }} />
                        ))}

                        {/* Handle (left side) */}
                        <div style={{
                          position: "absolute", left: 5, top: "50%", transform: "translateY(-50%)",
                          width: 4, height: 18, borderRadius: 2,
                          background: isMine ? "#378ADD" : "#333",
                          boxShadow: isMine ? "0 0 6px rgba(55,138,221,0.6)" : "inset 0 1px 2px rgba(255,255,255,0.05)",
                        }} />

                        {/* Status indicator dot */}
                        <div style={{
                          position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)",
                          width: 6, height: 6, borderRadius: "50%",
                          background: isMine ? t.blue : isFull ? t.red : isPartial ? t.amber : "#333",
                          boxShadow: isMine ? `0 0 6px ${t.blue}` : isFull ? `0 0 4px ${t.red}` : isPartial ? `0 0 4px ${t.amber}` : "none",
                        }} />

                        {/* Status text */}
                        <div style={{ position: "absolute", left: 26, top: "50%", transform: "translateY(-50%)" }}>
                          <div style={{ fontSize: 10, color: isMine ? t.blueText : isFull ? "#f87171" : isPartial ? t.amber : "#444", fontWeight: isMine ? 700 : 400 }}>
                            {isMine ? "MON CASIER" : isFull ? "COMPLET" : isPartial ? "1 PLACE" : "LIBRE"}
                          </div>
                          {isPartial && locker.users?.[0] && (
                            <div style={{ fontSize: 9, color: "#555", marginTop: 1 }}>
                              {locker.users[0].name?.split(" ")[0] || "Membre"}
                            </div>
                          )}
                        </div>

                        {/* Number — bottom right */}
                        <div style={{
                          position: "absolute", bottom: 4, right: 6,
                          fontSize: 11, fontWeight: 800,
                          color: isMine ? t.blue : "#333",
                          letterSpacing: 0.5,
                        }}>{locker.number}</div>

                        {/* Hover glow overlay */}
                        {canSelect && (
                          <div style={{ position: "absolute", inset: 0, background: `linear-gradient(135deg, transparent, rgba(37,99,235,0.05))`, borderRadius: 5, pointerEvents: "none" }} />
                        )}
                      </div>
                    );
                  })}

                  {/* Column bottom bar */}
                  <div style={{ height: 8, background: "#222", borderRadius: 4, marginTop: 4, boxShadow: "inset 0 1px 3px rgba(0,0,0,0.5)" }} />
                </div>
              ))}
            </div>

            {/* Stats */}
            <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
              {[
                { label: "Libres", value: lockers.filter(l => getLockerStatus(l) === "free").length, color: t.green },
                { label: "1 place", value: lockers.filter(l => getLockerStatus(l) === "partial").length, color: t.amber },
                { label: "Complets", value: lockers.filter(l => getLockerStatus(l) === "full").length, color: t.red },
              ].map(stat => (
                <div key={stat.label} style={{ flex: 1, background: t.cardBg, border: `1px solid ${t.border}`, borderRadius: 10, padding: "10px 8px", textAlign: "center" }}>
                  <div style={{ fontSize: 18, fontWeight: 800, color: stat.color }}>{stat.value}</div>
                  <div style={{ fontSize: 10, color: t.text3, marginTop: 2 }}>{stat.label}</div>
                </div>
              ))}
            </div>

            {/* Info */}
            <div style={{ marginTop: 12, background: t.blueBg, border: `1px solid ${t.border}`, borderRadius: 14, padding: "12px 14px", fontSize: 12, color: t.blueText, lineHeight: 1.6 }}>
              ℹ️ Chaque casier peut accueillir <strong>1 ou 2 personnes</strong>. L'attribution est valable tant que votre abonnement est actif. Gratuit, inclus dans votre abonnement.
            </div>

            {error && (
              <div style={{ marginTop: 10, background: t.redBg, border: `1px solid ${t.redBorder}`, borderRadius: 10, padding: "10px 12px", fontSize: 12, color: t.red }}>
                ⚠️ {error}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Confirm modal */}
      {selected && (
        <div style={{ position: "fixed", inset: 0, zIndex: 1000, background: "rgba(0,0,0,0.7)", backdropFilter: "blur(6px)", display: "flex", alignItems: "flex-end", justifyContent: "center", padding: "0 0 24px" }}
          onClick={e => { if (e.target === e.currentTarget) setSelected(null); }}>
          <div style={{ width: "100%", maxWidth: 440, borderRadius: "20px 20px 16px 16px", background: t.cardBg, border: `1px solid ${t.border}`, boxShadow: "0 -4px 32px rgba(0,0,0,0.3)", padding: "24px 20px", animation: "slideUp 0.3s ease" }}>

            {/* Mini locker preview */}
            <div style={{ display: "flex", justifyContent: "center", marginBottom: 20 }}>
              <div style={{ position: "relative", width: 80, height: 100, background: "#111", border: "2px solid #333", borderRadius: 10, boxShadow: "0 0 30px rgba(37,99,235,0.3), inset 0 2px 8px rgba(0,0,0,0.8)" }}>
                {[20,40,60,80].map(y => (
                  <div key={y} style={{ position: "absolute", left: 0, right: 0, top: y, height: 1, background: "rgba(255,255,255,0.04)" }} />
                ))}
                <div style={{ position: "absolute", left: 7, top: "50%", transform: "translateY(-50%)", width: 5, height: 22, borderRadius: 2, background: "#444" }} />
                <div style={{ position: "absolute", bottom: 6, right: 8, fontSize: 13, fontWeight: 800, color: "#666" }}>{selected.number}</div>
                {selected.users?.length === 1 && (
                  <div style={{ position: "absolute", top: 8, right: 8, width: 8, height: 8, borderRadius: "50%", background: t.amber, boxShadow: `0 0 6px ${t.amber}` }} />
                )}
              </div>
            </div>

            <div style={{ textAlign: "center", marginBottom: 16 }}>
              <div style={{ fontSize: 18, fontWeight: 800, color: t.text1 }}>Casier #{selected.number}</div>
              <div style={{ fontSize: 12, color: t.text2, marginTop: 4 }}>
                {selected.users?.length === 1
                  ? `Partagé avec ${selected.users[0].name?.split(" ")[0] || "un membre"} — 1 place restante`
                  : "Casier entièrement libre"}
              </div>
            </div>

            <div style={{ background: t.bg3, borderRadius: 12, padding: "12px 14px", marginBottom: 16, fontSize: 13, color: t.text2, lineHeight: 1.7 }}>
              👤 Attribué à : <strong style={{ color: t.text1 }}>{fullName}</strong><br />
              📅 Durée : <strong style={{ color: t.text1 }}>Jusqu'à la fin de votre abonnement</strong><br />
              💰 Tarif : <strong style={{ color: t.green }}>Gratuit ✅</strong>
            </div>

            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={() => setSelected(null)}
                style={{ flex: 1, padding: "12px", borderRadius: 12, border: `1px solid ${t.border}`, background: t.bg3, color: t.text2, fontSize: 13, cursor: "pointer" }}>
                Annuler
              </button>
              <button onClick={handleAssign} disabled={confirming}
                style={{ flex: 2, padding: "12px", borderRadius: 12, border: "none", background: confirming ? t.bg3 : t.blue, color: confirming ? t.text3 : "#fff", fontSize: 13, fontWeight: 700, cursor: confirming ? "not-allowed" : "pointer" }}>
                {confirming ? "Attribution..." : "🔓 Prendre ce casier"}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes fadeIn { from{opacity:0} to{opacity:1} }
        @keyframes popIn { from{opacity:0;transform:scale(0.3)} to{opacity:1;transform:scale(1)} }
        @keyframes slideUp { from{opacity:0;transform:translateY(20px)} to{opacity:1;transform:translateY(0)} }
        @keyframes unlock { from{transform:scale(0) rotate(-180deg)} to{transform:scale(1) rotate(0)} }
        @keyframes glowPulse { 0%,100%{opacity:0.4;transform:scale(1)} 50%{opacity:1;transform:scale(1.05)} }
        @keyframes confetti0 { from{opacity:0;transform:translateY(0) scale(0)} to{opacity:1;transform:translateY(-120px) scale(1)} }
        @keyframes confetti1 { from{opacity:0;transform:translateY(0) scale(0)} to{opacity:1;transform:translateY(-80px) scale(1)} }
        @keyframes confetti2 { from{opacity:0;transform:translateY(0) scale(0)} to{opacity:1;transform:translateY(-160px) scale(1)} }
      `}</style>
    </div>
  );
}

const theme = {
  light: { bg:"#f8fafc",bg3:"#f1f5f9",border:"#e2e8f0",text1:"#0f172a",text2:"#64748b",text3:"#94a3b8",blue:"#2563eb",blueBg:"#eff6ff",blueText:"#1d4ed8",green:"#16a34a",greenBg:"#f0fdf4",amber:"#d97706",amberBg:"#fffbeb",amberText:"#b45309",amberBorder:"#fde68a",red:"#dc2626",redBg:"#fef2f2",redBorder:"#fecaca",cardBg:"#ffffff" },
  dark:  { bg:"#0f172a",bg3:"#1a2740",border:"#273549",text1:"#f1f5f9",text2:"#94a3b8",text3:"#475569",blue:"#378ADD",blueBg:"#1e3a5f",blueText:"#85B7EB",green:"#4ade80",greenBg:"#052e16",amber:"#fbbf24",amberBg:"#2a1e07",amberText:"#fcd34d",amberBorder:"#78350f",red:"#f87171",redBg:"#2d1515",redBorder:"#7f1d1d",cardBg:"#1e293b" },
};

const s = {
  toggleTrack: { width:36,height:20,borderRadius:20,cursor:"pointer",position:"relative",flexShrink:0 },
  toggleKnob:  { width:14,height:14,borderRadius:"50%",position:"absolute",top:2,left:2,transition:"transform 0.2s" },
};
